import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageOpen, Search } from 'lucide-react';
import { posApi } from '@/services/api';
import ReportExportButton from '@/components/reports/ReportExportButton';

export default function ExpiringStockSection() {
  const [days, setDays] = useState('30');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');

  const daysNum = Number(days) || 30;

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'Product'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Product' });
      return res.data.message || [];
    },
  });

  const today = new Date().toISOString().slice(0, 10);

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['expiringStock', daysNum, category, search],
    queryFn: async () => {
      const res = await posApi.getStockReport({
        from_date: today,
        to_date: today,
        by_batch: 1,
        expiring_within_days: daysNum,
        category: category || undefined,
        item: search.trim() || undefined,
      });
      return (res.data.message || []) as any[];
    },
  });

  const handleExport = () => {
    if (!rows.length) return;
    const headers = ['Item Code', 'Item Name', 'Batch', 'Expiry', 'Category', 'Qty'];
    const csvRows = rows.map((r) =>
      [
        r.item_code,
        `"${(r.item_name || '').replace(/"/g, '""')}"`,
        `"${(r.batch_no || '').replace(/"/g, '""')}"`,
        r.expiry_date || '',
        `"${(r.category_name || r.category || '').replace(/"/g, '""')}"`,
        r.available_stock,
      ].join(','),
    );
    const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expiring_stock_${daysNum}d_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[140px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Expiring within (days)</label>
          <input
            type="number"
            min="1"
            className="input-field"
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
        </div>
        <div className="min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select className="input-field" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {(categoriesData || []).map((c: any) => (
              <option key={c.name} value={c.name}>
                {c.category_name || c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="input-field pl-9"
              placeholder="Item or batch"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <ReportExportButton onClick={handleExport} disabled={!rows.length} />
      </div>

      <p className="text-xs text-gray-500">
        Batched products with remaining stock and expiry date within the next {daysNum} day(s).
      </p>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          Failed to load expiring stock. Batch tracking must be enabled on items.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Batch</th>
                <th className="px-4 py-3 font-medium">Expiry</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Qty</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    <PackageOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    No batches expiring in this window.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={`${r.batch || r.item_code}-${idx}`} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.item_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.item_code}</div>
                    </td>
                    <td className="px-4 py-3">{r.batch_no || '—'}</td>
                    <td className="px-4 py-3 font-medium text-amber-800">{r.expiry_date || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.category_name || r.category || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{r.available_stock}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
